import { graphql, useStaticQuery } from "gatsby";
import { Container, Col, Row } from "react-bootstrap";
import styled from "styled-components";
import { SiteMetaDataFields } from "../../fragments/NodeSiteMetadataFields";
import { colors } from "../ui/colors";
import { SocialIconQuery, SocialMediaIcons } from "wmk-social";
import { ContentfulImageQuery, Img, WmkImage } from "wmk-image";
import { Typography } from "../ui/Typography";
import {
  WMKIconFacebook,
  WMKIconInstagram,
  WMKIconLinkedIn,
  WMKIconPinterest,
  WMKIconTwitter,
  WMKIconYouTube
} from "../../fonts/watermark-font/WMKFont";
import React, { useState, useEffect, useRef } from "react";
import { buttonStyles } from "../ui/Buttons/WmkButton";
import { LinkQuery } from "../../fragments/NodeLinkFields";
import { FormContext } from "../context/FormContext";
import { WmkLink } from "wmk-link";

const FooterWrap = styled(Container)<{ width?: boolean; path: boolean }>`
  padding: 0;
  ${({ width, path }) =>
    width && path
      ? `position: relative;
  left: -12.5%;
  width: 75%;`
      : ""};
  .footer-icons {
    padding: 1rem 0;
    .logo a {
      text-align: center;
      display: block;
    }
    img {
      width: 240px;
    }
    justify-content: space-around;
  }
  .wmk-social-icon {
    color: ${colors.accentOrange.getHex()};
    padding: 1rem 0.5rem;
    font-size: 2rem;
    &:hover {
      color: ${colors.accentRed.getHex()};
    }
  }
  .social-icons {
    display: flex;
    align-items: center;
  }
  .col {
    display: flex;
    justify-content: center;
    text-align: center;
  }
`;

const BackgroundImage = styled.div<{ height: number }>`
  img {
    ${({ height }) => (height ? `min-height: ${height}px` : "")};
  }
  position: absolute;
  z-index: -1;
`;

const WatermarkButton = styled.div`
  // position: absolute;
  // bottom: 0;
  margin: 2rem 0 1rem;
  & > div {
    display: flex !important;
    width: 100%;
    text-align: center;
    // color: ${colors.accentOrange.getHex()};
    cursor: pointer;
  }
  button {
    ${buttonStyles};
    background: ${colors.accentOrange.getHex()};
    color: ${colors.white.getHex()};
    min-width: 18.7rem;
    width: 100%;
  }
`;

const Footer = ({ is404 }: { is404?: boolean }) => {
  const [viewWidth, setViewWidth] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [path, setPath] = useState("");

  const footerDetailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleResize = () => {
    const winWidth = window.innerWidth;
    setViewWidth(winWidth);
  };

  useEffect(() => {
    const current = footerDetailRef.current;
    const handleResize = () => {
      const rect = current ? current.getBoundingClientRect() : null;
      if (rect) {
        setContentHeight(rect.height);
      }
    };
    window.addEventListener("resize", handleResize);
    const timeout = setTimeout(handleResize, 0);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeout);
    };
  });

  const data: FooterQuery = useStaticQuery(query);

  useEffect(() => {
    const path = location?.pathname ? location.pathname : "";
    setPath(path);
  });

  const platforms = {
    facebook: <WMKIconFacebook />,
    instagram: <WMKIconInstagram />,
    pinterest: <WMKIconPinterest />,
    twitter: <WMKIconTwitter />,
    youtube: <WMKIconYouTube />,
    linkedin: <WMKIconLinkedIn />
  };
  const backgroundLogo = new Img(data.background);
  return (
    <FormContext.Consumer>
      {(consumerData) => {
        // const user = data && "user" in data ? data.user : null;

        return (
          <FooterWrap
            fluid
            width={viewWidth > 991 ? "true" : ""}
            path={
              is404
                ? ""
                : path === "/crm" || path === "/inbound"
                ? ""
                : path !== "/" && !path.includes("/courses")
                ? "true"
                : ""
            }>
            <BackgroundImage height={contentHeight}>
              <WmkImage image={backgroundLogo} />
            </BackgroundImage>
            <Container fluid ref={footerDetailRef}>
              <Row>
                <Col className="text-column">
                  <Typography.BLOCKQUOTE
                    style={{
                      fontFamily: "Montserrat",
                      color: colors.accentOrange.getHex(),
                      margin: "2rem 0 0.75rem"
                    }}>
                    {data?.footer?.subheader}
                  </Typography.BLOCKQUOTE>
                </Col>
              </Row>
              <Row>
                <Col className="text-column">
                  <Typography.H2
                    style={{ fontFamily: "Rajdhani", fontWeight: 600 }}>
                    {data?.footer?.header}
                  </Typography.H2>
                </Col>
              </Row>
              <Row>
                <Col className="text-column">
                  <Typography.P
                    style={{
                      color: colors.textGray.getHex(),
                      padding: "0 2rem"
                    }}>
                    {data?.footer?.description}
                  </Typography.P>
                </Col>
              </Row>
              <Row>
                <Col>
                  <WatermarkButton>
                    <WmkLink
                      to={
                        data.footer.link.path.includes("http")
                          ? `${data.footer.link.path}`
                          : `/${data.footer.link.path}`
                      }
                      target="_blank">
                      <button>{data.footer.link.linkText}</button>
                    </WmkLink>
                  </WatermarkButton>
                </Col>
              </Row>
              <Row className="footer-icons">
                <Col style={{ margin: "0 0 2rem" }}>
                  <SocialMediaIcons
                    socials={data?.footer?.media?.links}
                    platforms={platforms}
                  />
                </Col>
              </Row>
            </Container>
          </FooterWrap>
        );
      }}
    </FormContext.Consumer>
  );
};

export default Footer;

interface FooterQuery {
  site: SiteMetaDataFields;
  footer: {
    header: string;
    subheader: string;
    description: string;
    media: {
      title: string;
      links: SocialIconQuery[];
    };
    link: LinkQuery;
  };
  background: ContentfulImageQuery;
}

const query = graphql`
  {
    footer: contentfulFooter(contentfulid: { eq: "Main Footer" }) {
      header
      subheader
      description
      media {
        title
        links {
          name: title
          title: linkText
          url: path
          target
        }
      }
      link {
        ...NodeLinkFields
      }
    }
    background: contentfulAsset(title: { eq: "Watermark logo background" }) {
      gatsbyImageData(quality: 80)
      title
      file {
        url
        contentType
      }
      description
    }
    site {
      ...NodeSiteMetadata
    }
  }
`;
